import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  ModelledProductIndexQuery,
  WorkspaceProductsQuery,
} from '@graphql/queries/PerceptionModelQueries';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  TextField,
  Typography,
} from '@mui/material';

import { anchorOptions, modelledProductIds } from './normSetUtils';
import PerceptionModelError from './PerceptionModelError';

/**
 * Pick the anchor products a norm set is measured from.
 *
 * Only products the perception model was built from are offered. A product with
 * no lookup row has no peer list, so it cannot anchor anything — offering it and
 * explaining the failure afterwards wastes the analyst's time, and workspaces
 * are mostly unmodelled products (7,988 vs 17 in one real case).
 *
 * Selection is by name because the model universe is keyed on name, but the
 * filter joins on product id via the lookup's product_ids, so a same-named
 * product belonging to another producer does not sneak into the list.
 *
 * ## Built for tens of anchors, not three
 *
 * The chips used to live inside the input, which is fine at three and unusable
 * at thirty: they push the search field down to a few characters, so finding
 * the next product costs a scroll past everything already found. So the input
 * is only ever a search box, and the selection is its own reviewable list
 * underneath, bounded in height and removable one at a time or all at once.
 *
 * Options carry a checkbox for the same reason. At three anchors the highlight
 * on a selected row is enough; at thirty, in a workspace whose names differ by
 * a hyphen ("Austria Red Bull" and "Austria-Red Bull"), it is not, and clicking
 * one to see what happens silently drops an anchor.
 */

type WorkspaceProduct = {
  id: number;
  name: string;
  brand: string | null;
  category: { id: number; name: string } | null;
};

type Props = {
  producerId: number;
  /** the job whose universe defines what can anchor */
  jobId?: number;
  value: string[];
  onChange: (names: string[]) => void;
  /** names with no lookup row for the active job */
  missing?: string[];
};

/**
 * How many matches are drawn at once.
 *
 * MUI's listbox is not virtualised, so a catalogue-wide match renders every row
 * into the DOM. The cap keeps that bounded; the count above it says how much is
 * being held back, since a silently truncated list reads as "it isn't there".
 */
const MENU_CAP = 100;

/** What tells two products with near-identical names apart. */
const describe = (products: WorkspaceProduct[]): string => {
  const parts = Array.from(
    new Set(
      products.flatMap((p) => [p.brand, p.category?.name].filter(Boolean)),
    ),
  );
  const suffix =
    products.length > 1 ? `${products.length} products with this name` : '';
  return [...parts, suffix].filter(Boolean).join(' · ');
};

const AnchorPicker: React.FC<Props> = ({
  producerId,
  jobId,
  value,
  onChange,
  missing = [],
}) => {
  const [query, setQuery] = React.useState('');

  const { data, loading } = useQuery(WorkspaceProductsQuery, {
    variables: { producerId },
    skip: !producerId,
  });

  const {
    data: modelData,
    loading: modelLoading,
    error: modelError,
  } = useQuery(ModelledProductIndexQuery, {
    variables: { jobId },
    skip: !jobId,
  });

  const products: WorkspaceProduct[] = data?.allProducts?.nodes ?? [];

  const modelled = React.useMemo(
    () =>
      modelledProductIds(
        modelData?.allPerceptionModelProductLookups?.nodes ?? [],
      ),
    [modelData],
  );

  const options = React.useMemo(
    () => anchorOptions(products, modelled),
    [products, modelled],
  );

  // Names are not unique, so what is behind one is what tells it from its
  // near-namesake in the list.
  const byName = React.useMemo(() => {
    const index = new Map<string, WorkspaceProduct[]>();
    products.forEach((p) => {
      if (!modelled.has(p.id)) return;
      index.set(p.name, [...(index.get(p.name) ?? []), p]);
    });
    return index;
  }, [products, modelled]);

  const selected = React.useMemo(() => new Set(value), [value]);

  // Filtered here rather than by the Autocomplete so the total is known: the
  // list is capped, and how much was cut is worth saying.
  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((name) => name.toLowerCase().includes(q));
  }, [options, query]);

  const visible = React.useMemo(() => matches.slice(0, MENU_CAP), [matches]);

  const busy = loading || modelLoading;
  // Never conclude "nothing is modelled" from a request that failed — an empty
  // response and an absent one look identical here, and the wrong one of those
  // disables the whole builder.
  const noneModelled =
    !busy && !modelError && products.length > 0 && options.length === 0;

  const unselectedMatches = matches.filter((name) => !selected.has(name));

  const addAll = () =>
    onChange(Array.from(new Set([...value, ...unselectedMatches])).sort());

  if (modelError) {
    return (
      <Box>
        <PerceptionModelError error={modelError} />
      </Box>
    );
  }

  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        // The input is a search box and stays one. Chips inside it squeeze the
        // field to nothing past a handful of anchors; the selection is its own
        // section below, where it can be read and pruned.
        renderTags={() => null}
        options={options}
        value={value}
        inputValue={query}
        // MUI clears the input after every pick (`reason: 'reset'`). With the
        // query controlled here that would throw away the search the moment it
        // was used, dropping the menu back to the whole catalogue and taking
        // the bulk-add button with it — the exact cost this is meant to remove.
        onInputChange={(_, next, reason) => {
          if (reason !== 'reset') setQuery(next);
        }}
        // Filtering is done above so the match total is available; MUI is left
        // to render what it is given.
        filterOptions={() => visible}
        loading={busy}
        disabled={noneModelled}
        onChange={(_, next) => onChange(([...next] as string[]).sort())}
        ListboxProps={{ style: { maxHeight: 320 } }}
        renderOption={(props, name, { selected: isSelected }) => {
          const detail = describe(byName.get(name) ?? []);
          return (
            <li {...props} key={name}>
              <Checkbox size="small" checked={isSelected} sx={{ mr: 1 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap title={name}>
                  {name}
                </Typography>
                {detail && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                    noWrap
                  >
                    {detail}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            // MUI deletes the last selected value on Backspace over an empty
            // input, which is how its in-field chips are removed. The chips are
            // not in the field here, so there is nothing under the caret to
            // explain a vanishing anchor and holding the key would empty the
            // selection. Stopping propagation leaves the keystroke to the text
            // box and keeps it from the Autocomplete's own handler; clicking a
            // row again, or a chip's delete, is how one is dropped.
            onKeyDown={(e) => {
              if (!query && (e.key === 'Backspace' || e.key === 'Delete')) {
                e.stopPropagation();
              }
            }}
            variant="outlined"
            label="Anchor products"
            placeholder={
              busy ? 'Loading…' : 'Search products in this perception model'
            }
          />
        )}
      />

      {!busy && !noneModelled && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mt: 0.5,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {matches.length === options.length
              ? `${options.length} of ${products.length} workspace products are in this perception model.`
              : `${matches.length} of ${options.length} match “${query.trim()}”.`}
            {matches.length > MENU_CAP &&
              ` Showing the first ${MENU_CAP} — keep typing to narrow it.`}
          </Typography>

          {/* Only offered against a search. Un-narrowed it would be "anchor on
              the entire workspace", which is not a comparison.
              preventDefault keeps focus in the field: blurring it would clear
              the query, and the button would vanish under the pointer before
              the click landed. */}
          {query.trim() && unselectedMatches.length > 0 && (
            <Button
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={addAll}
              sx={{ textTransform: 'none' }}
            >
              Add {unselectedMatches.length} matching
            </Button>
          )}
        </Box>
      )}

      {value.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography variant="body2">
              {value.length} anchor{value.length === 1 ? '' : 's'} selected
            </Typography>
            <Button
              size="small"
              onClick={() => onChange([])}
              sx={{ textTransform: 'none' }}
            >
              Clear all
            </Button>
          </Box>

          {/* Bounded, so fifty anchors are a panel to scroll rather than a page
              that pushes the save button out of sight. */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              mt: 0.5,
              p: 1,
              maxHeight: 168,
              overflowY: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {value.map((name) => (
              <Chip
                key={name}
                size="small"
                label={name}
                color={missing.includes(name) ? 'warning' : 'default'}
                onDelete={() => onChange(value.filter((n) => n !== name))}
              />
            ))}
          </Box>
        </Box>
      )}

      {noneModelled && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          None of this workspace's {products.length} products are in the
          perception model, so no norm set can be anchored here yet. They need
          to be reviewed and picked up by the next training run.
        </Alert>
      )}

      {missing.length > 0 && (
        <Typography
          variant="caption"
          sx={{ color: 'warning.main', display: 'block', mt: 1 }}
        >
          Not in this perception model, so they cannot anchor a norm set:{' '}
          {missing.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

export default AnchorPicker;
